/**
 * Table component displaying all lessons with their prompt status
 */

import { useState, useMemo } from 'react';
import { LessonWithPromptStatus } from '@/types/lessonPrompt';
import { Database, FileCode, Search, ChevronUp, ChevronDown, Edit2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface PromptListTableProps {
  lessons: LessonWithPromptStatus[];
  loading: boolean;
  onSelectLesson: (lessonId: string) => void;
}

type SortField = 'id' | 'title' | 'promptSource' | 'lastUpdated';
type SortDirection = 'asc' | 'desc';

export function PromptListTable({ lessons, loading, onSelectLesson }: PromptListTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [filterSource, setFilterSource] = useState<'all' | 'database' | 'file'>('all');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedLessons = useMemo(() => {
    let result = [...lessons];

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (lesson) =>
          lesson.id.toLowerCase().includes(query) ||
          lesson.title.toLowerCase().includes(query) ||
          lesson.displayTitle?.toLowerCase().includes(query)
      );
    }

    // Filter by source
    if (filterSource !== 'all') {
      result = result.filter((lesson) => lesson.promptSource === filterSource);
    }

    // Sort
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'id':
          comparison = a.id.localeCompare(b.id);
          break;
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'promptSource':
          comparison = a.promptSource.localeCompare(b.promptSource);
          break;
        case 'lastUpdated':
          const aDate = a.lastUpdated ? new Date(a.lastUpdated).getTime() : 0;
          const bDate = b.lastUpdated ? new Date(b.lastUpdated).getTime() : 0;
          comparison = aDate - bDate;
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [lessons, searchQuery, sortField, sortDirection, filterSource]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4" />
    ) : (
      <ChevronDown className="w-4 h-4" />
    );
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by ID or title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="flex gap-2">
          <Button
            variant={filterSource === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterSource('all')}
          >
            All ({lessons.length})
          </Button>
          <Button
            variant={filterSource === 'database' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterSource('database')}
          >
            <Database className="w-4 h-4 mr-1" />
            Database ({lessons.filter((l) => l.promptSource === 'database').length})
          </Button>
          <Button
            variant={filterSource === 'file' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterSource('file')}
          >
            <FileCode className="w-4 h-4 mr-1" />
            File ({lessons.filter((l) => l.promptSource === 'file').length})
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th
                className="px-4 py-3 text-left text-sm font-medium text-gray-500 cursor-pointer hover:text-gray-700"
                onClick={() => handleSort('id')}
              >
                <div className="flex items-center gap-1">
                  Lesson ID
                  <SortIcon field="id" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-sm font-medium text-gray-500 cursor-pointer hover:text-gray-700"
                onClick={() => handleSort('title')}
              >
                <div className="flex items-center gap-1">
                  Title
                  <SortIcon field="title" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-sm font-medium text-gray-500 cursor-pointer hover:text-gray-700"
                onClick={() => handleSort('promptSource')}
              >
                <div className="flex items-center gap-1">
                  Source
                  <SortIcon field="promptSource" />
                </div>
              </th>
              <th
                className="px-4 py-3 text-left text-sm font-medium text-gray-500 cursor-pointer hover:text-gray-700"
                onClick={() => handleSort('lastUpdated')}
              >
                <div className="flex items-center gap-1">
                  Last Updated
                  <SortIcon field="lastUpdated" />
                </div>
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedLessons.map((lesson) => (
              <tr
                key={lesson.id}
                className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                onClick={() => onSelectLesson(lesson.id)}
              >
                <td className="px-4 py-3">
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded text-gray-700">
                    {lesson.id}
                  </code>
                </td>
                <td className="px-4 py-3">
                  <div>
                    <div className="font-medium text-gray-900">{lesson.title}</div>
                    {lesson.displayTitle && (
                      <div className="text-sm text-gray-500">{lesson.displayTitle}</div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {lesson.promptSource === 'database' ? (
                    <Badge variant="default" className="bg-purple-100 text-purple-700">
                      <Database className="w-3 h-3 mr-1" />
                      Database
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-gray-500">
                      <FileCode className="w-3 h-3 mr-1" />
                      File Default
                    </Badge>
                  )}
                </td>
                <td className="px-4 py-3">
                  {lesson.lastUpdated ? (
                    <div>
                      <div className="text-sm text-gray-900">{formatDate(lesson.lastUpdated)}</div>
                      {lesson.updatedByName && (
                        <div className="text-xs text-gray-500">by {lesson.updatedByName}</div>
                      )}
                    </div>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectLesson(lesson.id);
                    }}
                  >
                    <Edit2 className="w-4 h-4 mr-1" />
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredAndSortedLessons.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No lessons found matching your criteria
          </div>
        )}
      </div>
    </div>
  );
}
